import { InfoOutlined } from "@mui/icons-material";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { useContainerWidth } from "../../../../components/LineChart/hooks/useContainerWidth";
import { Tooltip } from "../../../../components/Tooltip";
import { useLocalization } from "../../../../hooks/useLocalization";
import { useTooltipAnimation } from "../../../../hooks/useTooltipAnimation";
import { useScrollbarDebounce } from "../hooks/useScrollbarDebounce";
import {
  FeedbackMetricType,
  type ExperienceFeedbackRating,
  type FeedbackChartDatum,
  type FeedbackTooltipPayloadItem,
} from "../types";
import { formatExperienceName } from "../utils/usageUtils";
import { LabeledSelectField } from "./LabeledSelectField";
import { StarRating } from "./StarRating";

const MIN_FEEDBACK_COUNT = 5;

interface CustomBarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: FeedbackChartDatum;
  countKey: keyof Pick<FeedbackChartDatum, "assessmentCount" | "practiceCount">;
  isUserLevelDataEnabled?: boolean;
  fill: string;
}

const CustomBarShape: React.FC<CustomBarShapeProps> = (props) => {
  const {
    x,
    y,
    width,
    height,
    payload,
    countKey,
    isUserLevelDataEnabled,
    fill,
  } = props;
  const count = payload?.[countKey];
  const valueKey = countKey === "practiceCount" ? "practice" : "assessment";
  const value = payload?.[valueKey];

  const isEmpty = value === 0 && count === 0;
  const isBelowThreshold =
    !isUserLevelDataEnabled &&
    count !== undefined &&
    count < MIN_FEEDBACK_COUNT;

  const opacity = isEmpty || isBelowThreshold ? 0.5 : 1;

  return (
    <rect
      fill={fill}
      height={height}
      opacity={opacity}
      width={width}
      x={x}
      y={y}
    />
  );
};

export const formatFeedbackCount = (
  count: number,
  isUserLevelDataEnabled: boolean,
): string => {
  if (!isUserLevelDataEnabled && count < MIN_FEEDBACK_COUNT) {
    return `<${MIN_FEEDBACK_COUNT}`;
  }

  return String(count);
};

export const getFeedbackChartRating = (
  feedbackCount: number,
  rating: number | null,
  isUserLevelDataEnabled = false,
): number => {
  if (rating === null) {
    return 0;
  }

  if (isUserLevelDataEnabled) {
    return rating;
  }

  return feedbackCount >= MIN_FEEDBACK_COUNT ? rating : 0;
};

const CHART_MARGIN = { top: 20, right: 26, left: 0, bottom: 10 } as const;
const BAR_MAX_SIZE = 24;
const BAR_GAP = 4;
const BAR_CATEGORY_GAP_RATIO = 0.2;
const BAR_CATEGORY_GAP_RATIO_SM = 0.4;
const X_AXIS_HEIGHT = 100;
const CATEGORY_WIDTH = 80;
const Y_AXIS_WIDTH = 44;
const MAX_RATING = 5;
const X_AXIS_PADDING = 40;

const VIRTUALIZATION_THRESHOLD = 30;
const VIRTUALIZATION_CHUNK = 10;

type SortOption = FeedbackMetricType | "name";

// ── Custom X-Axis Tick ──────────────────────────────────────────────

const CustomXAxisTick: React.FC<{
  x?: number;
  y?: number;
  payload?: { value: string };
  fill?: string;
}> = ({ x = 0, y = 0, payload, fill }) => {
  const theme = useTheme();
  if (!payload?.value) return null;

  const lines = formatExperienceName(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        fill={fill ?? theme.colors.text[1]}
        fontSize={10}
        textAnchor="end"
        transform="rotate(-50)"
      >
        {lines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tspan key={`line-${line}-${index}`} x={0} y={15 + index * 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const XAxisTickWithMap: React.FC<{
  x?: number;
  y?: number;
  payload?: { value: string };
  fill?: string;
  chartIdToName?: Record<string, string>;
}> = ({ x, y, payload, fill, chartIdToName = {} }) => (
  <CustomXAxisTick
    fill={fill}
    payload={{
      value: chartIdToName[payload?.value ?? ""] ?? payload?.value ?? "",
    }}
    x={x}
    y={y}
  />
);

// ── Custom Tooltip ──────────────────────────────────────────────────

export const FeedbackChartTooltip: React.FC<{
  active?: boolean;
  payload?: FeedbackTooltipPayloadItem[];
  label?: string;
  getMetricLabel: (m: FeedbackMetricType) => string;
  minimumRatingsMessage: string;
  isUserLevelDataEnabled: boolean;
}> = ({
  active,
  payload,
  label,
  getMetricLabel,
  minimumRatingsMessage,
  isUserLevelDataEnabled,
}) => {
  const theme = useTheme();
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
        maxWidth: 280,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 13,
          mb: 1,
          wordBreak: "break-word",
          color: theme.colors.text[6],
        }}
      >
        {payload[0]?.payload.name ?? label}
      </Typography>
      {payload.map((entry) => {
        const countKey =
          entry.dataKey === FeedbackMetricType.PRACTICE
            ? "practiceCount"
            : "assessmentCount";
        const count = entry.payload[countKey];

        const isEmpty = entry.value === 0 && count === 0;
        const hasSufficientData =
          isUserLevelDataEnabled || count >= MIN_FEEDBACK_COUNT;
        const displayValue = hasSufficientData && !isEmpty;

        return (
          <Box key={entry.dataKey} sx={{ mb: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 12, color: theme.colors.text[6] }}>
                {getMetricLabel(entry.dataKey)}:{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {displayValue ? entry.value.toFixed(1) : "N/A"}
                </Box>{" "}
                ({formatFeedbackCount(count, isUserLevelDataEnabled)})
              </Typography>
            </Box>
            {hasSufficientData && (
              <Box sx={{ ml: "18px" }}>
                <StarRating rating={entry.value} size={14} />
              </Box>
            )}
            {!hasSufficientData && (
              <Typography
                sx={{
                  fontSize: 10,
                  color: theme.colors.text[0],
                  ml: "18px",
                }}
              >
                {minimumRatingsMessage}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Fixed Y-Axis ────────────────────────────────────────────────────

const FixedFeedbackYAxis: React.FC<{
  height: number;
}> = ({ height }) => {
  const theme = useTheme();
  const ticks = [0, 1, 2, 3, 4, 5];

  return (
    <ResponsiveContainer height={height} width="100%">
      <BarChart
        data={[{ axis: "axis", value: 0 }]}
        margin={{
          top: CHART_MARGIN.top,
          right: 0,
          left: 0,
          bottom: CHART_MARGIN.bottom,
        }}
      >
        <XAxis
          axisLine={false}
          dataKey="axis"
          height={X_AXIS_HEIGHT}
          interval={0}
          tick={false}
          tickLine={false}
        />
        <YAxis
          allowDataOverflow
          axisLine={{ stroke: theme.colors.secondary[3] }}
          domain={[0, MAX_RATING]}
          interval={0}
          scale="auto"
          tick={{
            fill: theme.colors.text[3],
            fontSize: 10,
            fontFamily: theme.typography.fontFamily as string,
          }}
          tickLine={false}
          ticks={ticks}
          type="number"
          width={Y_AXIS_WIDTH}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Main Component ──────────────────────────────────────────────────

interface FeedbackRatingsChartProps {
  data: ExperienceFeedbackRating[];
  sortSlotElement?: HTMLElement | null;
  isUserLevelDataEnabled?: boolean;
}

export const FeedbackRatingsChart: React.FC<FeedbackRatingsChartProps> = ({
  data,
  sortSlotElement,
  isUserLevelDataEnabled = false,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();

  const { ref: containerRef, width: containerWidth } =
    useContainerWidth<HTMLDivElement>(false);

  useTooltipAnimation(containerRef);

  const scrollbarSx = useScrollbarDebounce(containerWidth);

  const availableMetrics = useMemo(
    () => [FeedbackMetricType.PRACTICE, FeedbackMetricType.ASSESSMENT],
    [],
  );

  const metricColors: Record<FeedbackMetricType, string> = useMemo(
    () => ({
      [FeedbackMetricType.PRACTICE]: theme.colors.feedback.practice,
      [FeedbackMetricType.ASSESSMENT]: theme.colors.feedback.assessment,
    }),
    [theme.colors.feedback],
  );

  const [selectedMetrics, setSelectedMetrics] =
    useState<FeedbackMetricType[]>(availableMetrics);
  const [sortBy, setSortBy] = useState<SortOption>(
    FeedbackMetricType.ASSESSMENT,
  );

  const handleMetricChange = (metric: FeedbackMetricType) => {
    setSelectedMetrics((prev) => {
      const next = prev.includes(metric)
        ? prev.filter((m) => m !== metric)
        : [...prev, metric];

      next.sort(
        (a, b) => availableMetrics.indexOf(a) - availableMetrics.indexOf(b),
      );

      return next;
    });
  };

  const metricLabels: Record<FeedbackMetricType, string> = useMemo(
    () => ({
      [FeedbackMetricType.PRACTICE]: t(
        "pages.dataAnalytics.internalUsage.feedback.metrics.practice",
      ),
      [FeedbackMetricType.ASSESSMENT]: t(
        "pages.dataAnalytics.internalUsage.feedback.metrics.assessment",
      ),
    }),
    [t],
  );

  const metricTooltips: Record<FeedbackMetricType, string> = useMemo(
    () => ({
      [FeedbackMetricType.PRACTICE]: t(
        "pages.dataAnalytics.internalUsage.feedback.tooltips.practice",
      ),
      [FeedbackMetricType.ASSESSMENT]: t(
        "pages.dataAnalytics.internalUsage.feedback.tooltips.assessment",
      ),
    }),
    [t],
  );

  const getMetricLabel = useCallback(
    (metric: FeedbackMetricType) => metricLabels[metric],
    [metricLabels],
  );

  const minimumRatingsMessage = t(
    "pages.dataAnalytics.internalUsage.feedback.minimumRatings",
  );

  const chartData: FeedbackChartDatum[] = useMemo(() => {
    const mapped = data.map((item) => ({
      id: item.experienceId ?? item.experienceName,
      name: item.experienceName,
      practice: getFeedbackChartRating(
        item.practiceFeedbackCount,
        item.practiceRating,
        isUserLevelDataEnabled,
      ),
      assessment: getFeedbackChartRating(
        item.assessmentFeedbackCount,
        item.assessmentRating,
        isUserLevelDataEnabled,
      ),
      practiceCount: item.practiceFeedbackCount,
      assessmentCount: item.assessmentFeedbackCount,
    }));

    const sorted = [...mapped];
    switch (sortBy) {
      case FeedbackMetricType.PRACTICE:
        sorted.sort((a, b) => b.practice - a.practice);
        break;
      case FeedbackMetricType.ASSESSMENT:
        sorted.sort((a, b) => b.assessment - a.assessment);
        break;
      case "name":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return sorted;
  }, [data, sortBy, isUserLevelDataEnabled]);

  const chartWidth = useMemo(
    () => (chartData.length > 0 ? chartData.length * CATEGORY_WIDTH : 0),
    [chartData.length],
  );

  const chartIdToName = useMemo(
    () =>
      Object.fromEntries(chartData.map((d) => [d.id, d.name])) as Record<
        string,
        string
      >,
    [chartData],
  );

  const chartHeight = 390;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollChunk, setScrollChunk] = useState(0);

  const isVirtualized = chartData.length > VIRTUALIZATION_THRESHOLD;
  const viewportWidth = Math.max(
    (containerWidth || globalThis.innerWidth) - Y_AXIS_WIDTH - 0.5,
    0,
  );
  const totalWidth = Math.max(chartWidth, viewportWidth);
  const categoryWidth =
    (totalWidth - CHART_MARGIN.left - CHART_MARGIN.right - 2 * X_AXIS_PADDING) /
    Math.max(chartData.length, 1);
  const chunkWidth = VIRTUALIZATION_CHUNK * categoryWidth;

  const syncScrollChunk = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !isVirtualized) return;
    setScrollChunk(Math.floor(el.scrollLeft / chunkWidth));
  }, [isVirtualized, chunkWidth]);

  useLayoutEffect(syncScrollChunk, [syncScrollChunk, chartData.length]);

  const visibleWindow = useMemo(() => {
    if (!isVirtualized) {
      return { start: 0, end: chartData.length };
    }

    const start = Math.max(0, (scrollChunk - 1) * VIRTUALIZATION_CHUNK);
    const end = Math.min(
      chartData.length,
      start +
        Math.ceil(viewportWidth / categoryWidth) +
        3 * VIRTUALIZATION_CHUNK,
    );

    return { start, end };
  }, [
    isVirtualized,
    scrollChunk,
    chartData.length,
    viewportWidth,
    categoryWidth,
  ]);

  const visibleChartData = useMemo(
    () => chartData.slice(visibleWindow.start, visibleWindow.end),
    [chartData, visibleWindow],
  );

  const sortOptions = useMemo(
    () =>
      [
        ...selectedMetrics.map((metric) => ({
          value: metric as SortOption,
          label: getMetricLabel(metric),
        })),
        {
          value: "name" as SortOption,
          label: t(
            "pages.dataAnalytics.internalUsage.feedback.sortOptions.name",
          ),
        },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [selectedMetrics, getMetricLabel, t],
  );

  const renderChartContents = (paddingLeft: number, paddingRight: number) => (
    <>
      <CartesianGrid stroke={theme.colors.secondary[1]} vertical={true} />
      <XAxis
        axisLine={{ stroke: theme.colors.secondary[3] }}
        dataKey="id"
        height={X_AXIS_HEIGHT}
        interval={0}
        padding={{ left: paddingLeft, right: paddingRight }}
        tick={
          <XAxisTickWithMap
            chartIdToName={chartIdToName}
            fill={theme.colors.text[3]}
          />
        }
        tickLine={false}
        tickMargin={8}
      />
      <YAxis
        domain={[1, MAX_RATING]}
        hide={true}
        interval={0}
        scale="auto"
        ticks={[1, 2, 3, 4, 5]}
        type="number"
        width={0}
      />
      <RechartsTooltip
        content={
          <FeedbackChartTooltip
            getMetricLabel={getMetricLabel}
            isUserLevelDataEnabled={isUserLevelDataEnabled}
            minimumRatingsMessage={minimumRatingsMessage}
          />
        }
        cursor={{ fill: theme.palette.action.hover }}
      />
      {availableMetrics.map((metric) => {
        const isSelected = selectedMetrics.includes(metric);

        return (
          <Bar
            dataKey={metric}
            hide={!isSelected}
            key={metric}
            minPointSize={4}
            name={metric}
            radius={[2, 2, 0, 0]}
            shape={
              <CustomBarShape
                countKey={
                  metric === FeedbackMetricType.PRACTICE
                    ? "practiceCount"
                    : "assessmentCount"
                }
                fill={metricColors[metric]}
                isUserLevelDataEnabled={isUserLevelDataEnabled}
              />
            }
          />
        );
      })}
    </>
  );

  const barCategoryGap = `${
    (chartData.length < 4
      ? BAR_CATEGORY_GAP_RATIO_SM
      : BAR_CATEGORY_GAP_RATIO) * 100
  }%`;

  const renderVirtualizedChart = () => {
    const { start, end } = visibleWindow;
    const isFirst = start === 0;
    const isLast = end === chartData.length;
    const paddingLeft = isFirst ? X_AXIS_PADDING : 0;
    const paddingRight = isLast ? X_AXIS_PADDING : 0;
    const sliceLeft = isFirst ? 0 : X_AXIS_PADDING + start * categoryWidth;
    const sliceWidth =
      CHART_MARGIN.left +
      paddingLeft +
      (end - start) * categoryWidth +
      paddingRight +
      CHART_MARGIN.right;

    return (
      <div
        style={{
          height: chartHeight,
          left: sliceLeft,
          position: "absolute",
          top: 0,
          width: sliceWidth,
        }}
      >
        <BarChart
          barCategoryGap={barCategoryGap}
          barGap={BAR_GAP}
          data={visibleChartData}
          height={chartHeight}
          margin={CHART_MARGIN}
          maxBarSize={BAR_MAX_SIZE}
          width={sliceWidth}
        >
          {renderChartContents(paddingLeft, paddingRight)}
        </BarChart>
      </div>
    );
  };

  const renderMetricCheckboxes = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        justifyContent: "center",
      }}
    >
      {availableMetrics.map((metric) => (
        <Box
          key={metric}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: { md: 1 },
            justifyContent: { md: "center" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={selectedMetrics.includes(metric)}
              onChange={() => handleMetricChange(metric)}
              sx={{
                color: metricColors[metric],
                height: 20,
                width: 20,
                borderRadius: "4px",
                mr: "6px",
                "&.Mui-checked": { color: metricColors[metric] },
              }}
            />
            <Typography
              sx={{
                fontSize: 14,
                color: theme.colors.text[6],
                fontWeight: 500,
              }}
            >
              {getMetricLabel(metric)}
            </Typography>
          </Box>
          <Tooltip message={metricTooltips[metric]} placement="right">
            <InfoOutlined
              sx={{ fontSize: 20, color: theme.colors.secondary[6] }}
            />
          </Tooltip>
        </Box>
      ))}
    </Box>
  );

  if (selectedMetrics.length === 0) {
    return (
      <>
        <Box sx={{ mb: 3, pl: { xs: "8px", md: "60px" }, pr: "20px" }}>
          {renderMetricCheckboxes()}
        </Box>
        <Box
          sx={{
            p: 4,
            backgroundColor: theme.colors.secondary[0],
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          <Typography sx={{ color: theme.colors.text[1], fontSize: 14 }}>
            {t("pages.dataAnalytics.internalUsage.feedback.noMetricsSelected")}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {selectedMetrics.length > 0 &&
        (sortSlotElement ? (
          createPortal(
            <LabeledSelectField<SortOption>
              containerHeight={60}
              containerWidth={225}
              label={t("pages.dataAnalytics.internalUsage.feedback.sortBy")}
              onChange={setSortBy}
              options={sortOptions}
              selectWidth={225}
              value={sortBy}
            />,
            sortSlotElement,
          )
        ) : (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <LabeledSelectField<SortOption>
              containerHeight={60}
              containerWidth={225}
              label={t("pages.dataAnalytics.internalUsage.feedback.sortBy")}
              onChange={setSortBy}
              options={sortOptions}
              selectWidth={225}
              value={sortBy}
            />
          </Box>
        ))}
      <Box ref={containerRef} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${Y_AXIS_WIDTH + 0.5}px minmax(0, 1fr)`,
            alignItems: "start",
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: Y_AXIS_WIDTH + 0.5,
              height: chartHeight,
              minHeight: chartHeight,
              flexShrink: 0,
              overflow: "hidden",
              "& .recharts-wrapper *:focus": { outline: "none" },
            }}
          >
            <FixedFeedbackYAxis height={chartHeight} />
          </Box>
          <Box
            data-testid="feedback-chart-scroll"
            onScroll={isVirtualized ? syncScrollChunk : undefined}
            ref={scrollRef}
            sx={{ overflowX: "scroll", pb: 2, minWidth: 0, ...scrollbarSx }}
          >
            <Box
              sx={{
                width: isVirtualized ? totalWidth : chartWidth || "100%",
                minWidth: "100%",
                height: chartHeight,
                minHeight: chartHeight,
                position: "relative",
                "& .recharts-wrapper *:focus": { outline: "none" },
              }}
            >
              {isVirtualized ? (
                renderVirtualizedChart()
              ) : (
                <ResponsiveContainer height={chartHeight} width="100%">
                  <BarChart
                    barCategoryGap={barCategoryGap}
                    barGap={BAR_GAP}
                    data={chartData}
                    margin={CHART_MARGIN}
                    maxBarSize={BAR_MAX_SIZE}
                  >
                    {renderChartContents(X_AXIS_PADDING, X_AXIS_PADDING)}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 2, pl: { xs: "8px", md: "60px" }, pr: "20px" }}>
        {renderMetricCheckboxes()}
      </Box>
    </>
  );
};
